'use strict';

import React, {Component} from 'react';
import {observer} from "mobx-react";
import PropTypes from 'prop-types';

import { FaCheck, FaBan } from "react-icons/fa";

import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import ButtonToolbar from "react-bootstrap/ButtonToolbar";
import Form from "react-bootstrap/Form";
import Spinner from 'react-bootstrap/Spinner';

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import moveCourseOnPlanFormStore from "./MoveCourseOnPlanFormStore";
import courseSummaryStore from "./CourseSummaryStore";
import TermUtils from '@uw-it-sis/lib-react/lib/TermUtils.jsx';
import SimpleState from "@uw-it-sis/lib-react/lib/SimpleState";
import {isCoursePlaceholder} from './CourseUtils';
import {getGeneralPlaceholderName} from './GeneralPlaceHolderConstants';
import {MetricLabels} from "./Constants";

class MoveCourseOnPlanForm extends Component {

    constructor(props) {
        super(props);
        this.props = props;
        this.store = moveCourseOnPlanFormStore;

        this.analyticsStore = appStore.getAnalyticsStore();

        this.onChange = this.onChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.handleCancelClick = this.handleCancelClick.bind(this);
        this.buildDoneComponent = this.buildDoneComponent.bind(this);
    }

    componentDidMount() {
        let courseCode = "";
        let placeHolderText = "";
        // If its a course placeholder, placeholderCode is the course code
        // If its a general placeholder then  the expected courseCode is the '|' delimited gened type and the enum type
        if (this.props.data.placeHolder && isCoursePlaceholder(this.props.data.placeHolderCode)) {
            courseCode = this.props.data.placeHolderCode;
        } else if (this.props.data.placeHolder && this.props.data.planItemDataObject.refObjId && this.props.data.planItemDataObject.refObjType) {
            courseCode = this.props.data.planItemDataObject.refObjId + "|" + this.props.data.planItemDataObject.refObjType;
            placeHolderText = this.props.data.placeHolderValue;
        } else {
            courseCode = this.props.data.courseDetails.code;
        }
        this.store.mount(courseCode, this.props.planItemId, this.props.termId, this.props.data.note, this.props.data.credit, placeHolderText);
    }

    componentWillUnmount() {
        this.store.unmount();
    }

    /**
     * Callback to the parent component when the cancel link is clicked.
     */
    handleCancelClick(e) {
        this.props.onCancel();
    }

    /**
     * Handle input widget changes.
     */
    onChange(e) {
        let property = e.target.name;
        let value = undefined;
        if ( property === "backup" ) {
            value = e.target.checked;
        } else {
            value = e.target.value;
        }
        this.store.updateFormData(property, value);
    }

    /**
     * Handle form submit.
     */
    onSubmit(e) {
        e.preventDefault();  //  Prevent POST and reload.
        this.store.moveCourse();

        this.additionalAnalytics = this.props.additionalAnalytics && Object.keys(this.props.additionalAnalytics).length
            ? this.props.additionalAnalytics : {};
        let appContext = "";
        if(this.additionalAnalytics) {
            appContext = this.additionalAnalytics.context;
        }
        const labels = this.analyticsStore.label(MetricLabels.appContextName, appContext).get();
        this.analyticsStore.addEvent("Move-PlanItem", labels, {fromQtr: this.props.termId, toQtr: this.store.getFormData().termId,
            ...this.additionalAnalytics}
        );
    }

    handleSuccessLink(termId) {
        this.analyticsStore.addEvent("MoveSuccess-SqvLinkClick", {}, {qtr: termId});
        // In instances where the route does not change, this will close the modal along with the navigation
        // Without it, the route change happens, but the modal remains open.
        this.props.onCancel();
    }

    buildDoneComponent() {
        // map of term ids to names
        let futureTermNames = {};

        //  Build the term options list for the drop-down. Show only 12 terms max and disable the term the plan item current exists in
        let termOptions = this.store.getFutureTerms().filter((term, i) => i < 12).map(function (term, i) {
            futureTermNames[term.id] = term.name;
            return (
                <option key={i} value={term.id} disabled={term.id === this.props.termId}>{term.name}</option>
            );
        }.bind(this));

        //  We can avoid asking the course store to load this course because the course summary widget will
        //  have already asked for it and it will be cached in the course summary store. However, this seems a little
        //  risky.
        let courseSummary = this.props.data.courseDetails;
        const moveFailedMessage = this.store.getMoveFailedMessage();

        // item name used in the text of the modal to identify item being moved
        let itemName = "";
        // item label for the button action, typically itemName = itemLabel, except if item is other placeholder
        let itemLabel = "";
        if (this.props.data.placeHolder) {
            itemName = getGeneralPlaceholderName(this.props.data.placeHolderCode, this.props.data.placeHolderValue);
            itemLabel = getGeneralPlaceholderName(this.props.data.placeHolderCode, this.props.data.placeHolderValue, false)
            if ( this.props.data.planItemDataObject && this.props.data.planItemDataObject.refObjType === "uw.course.genedrequirement" ) {
                let placeholderPrefix = "Gen Ed Req: ";
                itemName = placeholderPrefix + itemName;
                itemLabel = placeholderPrefix + itemLabel;
            }
        } else {
            itemName = courseSummary.code;
            itemLabel = itemName;
        }
        // Successful move display for modal
        if ( this.store.isSubmitted() && moveFailedMessage === undefined && !this.store.isMoving() ) {
            let termName = futureTermNames[this.store.getFormData().termId];
            return (
                <>
                    <div className="d-flex align-items-start text-success mb-3">
                        <FaCheck className="me-3 fs-5" aria-hidden={true} />
                        <div>
                            <p className="mb-0"><strong>{itemName}</strong> has been moved.</p>
                            <p className="mb-0">
                                <a href={"/plan/#/" + TermUtils.makeTermLinkIdentifierFromTermId(this.store.getFormData().termId)}
                                   onClick={() => this.handleSuccessLink(this.store.getFormData().termId)}>
                                    View {termName} plan
                                </a>
                            </p>
                        </div>
                    </div>
                    <ButtonToolbar className="justify-content-end mt-3">
                        <Button variant="primary" id="move-planitem-done" onClick={this.handleCancelClick} type="button">Done</Button>
                    </ButtonToolbar>
                </>
            );
        }

        if ( ! courseSummary.scheduledTerms ) {
            courseSummary = courseSummaryStore.getCourseSummary(courseSummary.code);
        }

        //  Build the term badge.
        let scheduledTerms = <span>Not currently offered</span>;
        if (courseSummary.scheduledTerms.length > 0) {
            let scheduledTermList = courseSummary.scheduledTerms.map((term, i) => {
                return (
                    <li className="list-inline-item" key={i}>
                        {TermUtils.makeTermBadge(term, i)}
                    </li>
                );
            });

            scheduledTerms = <ul className="list-inline mb-0">{scheduledTermList}</ul>;
        }
        let scheduledFor = null;
        if ( !this.props.data.placeHolder){
            scheduledFor =
                <div className="my-3 d-flex align-items-center">
                    <strong className="me-2">Currently scheduled for:</strong>
                    <span>{scheduledTerms}</span>
                </div>
        }
        // Build messaging for if there was a failure to move
        let failureMessage;
        if ( moveFailedMessage && "" !== moveFailedMessage ) {
            failureMessage =
                <Alert variant="danger" className="mb-0 mt-3 d-flex align-items-start">
                    <FaBan className="me-2 mt-1" aria-hidden={true} />
                    <span>{moveFailedMessage}</span>
                </Alert>
        }

        let helpDefault = "This course MAY NOT be offered in your selected quarter. Check back after the Time Schedule is released.";
        let helpBlock = null;
        if ( !this.props.data.placeHolder) {
            helpBlock = <><span>Note:</span><br /><span>{helpDefault}</span></>;
            if (this.props.data.planActivities.length > 0 ) {
                helpBlock =
                    <>
                        <span>Note:</span><br />
                        <ul style={{paddingLeft: '1.25rem'}}>
                            <li>{helpDefault}</li>
                            <li>Section(s) you have selected for this course will be deleted when the course is moved to another quarter.</li>
                        </ul>
                    </>
            }

        }

        // Button labeling for move action that changes when in a pending state of moving
        let moveButtonLabel = <>Move {itemLabel}</>;
        if ( this.store.isMoving() ) {
            moveButtonLabel =
                <>
                    <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2 align-middle"
                    />
                    <span className="align-middle">Moving {itemLabel}</span>
                </>;
        }

        // PLANNING-116: Removed {scheduledFor} based on UX input, but leaving code in case minds get changed.

        return (
            <>
                <Form.Group controlId="move-planitem-term-options">
                    <Form.Label>Move {itemName} from {TermUtils.makeTermDescriptionFromTermId(this.props.termId)} to:</Form.Label>
                    <Form.Select
                        name="termId"
                        disabled={this.store.isMoving()}
                        onChange={this.onChange}>
                        {termOptions}
                    </Form.Select>
                    <Form.Text className="mt-3">
                        {helpBlock}
                    </Form.Text>
                </Form.Group>
                <ButtonToolbar className="justify-content-end mt-3">
                    <Button id="move-planitem-submit"
                            variant="primary"
                            onClick={this.store.isMoving() ? null : this.onSubmit}
                            disabled={this.store.isMoving()}>
                        {moveButtonLabel}
                    </Button>
                    <Button id="move-planitem-cancel" className="btn-secondary" onClick={this.handleCancelClick} type="reset">Cancel</Button>
                </ButtonToolbar>
                <div role="alert" aria-live="assertive" aria-atomic="true">
                    {failureMessage}
                </div>
            </>
        );
    }

    render() {
        return <SimpleState state={this.store.state} doneComponentBuilder={this.buildDoneComponent} name="MoveCourseOnPlanForm"/>
    }
}

MoveCourseOnPlanForm.propTypes = {
    planItemId: PropTypes.string.isRequired,
    termId: PropTypes.string.isRequired,
    // Going to have to refactor this later when items other then course and sections come into play
    data: PropTypes.object.isRequired,
    isSection: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    additionalAnalytics: PropTypes.object
};

export default observer(MoveCourseOnPlanForm);